import produce from 'immer';
import { MetricId } from '../../../../api/types-graphql';
import { toTimeSegmentLabel } from '../../../../api/utils';
import { useMetricDetailsMapContext } from '../../../../context/contexts';
import { MetricIdTypeArrays } from '../../../../types';
import {
  extractSegmentValue,
  useDataFieldValueFormatter,
} from '../../../hooks';
import { TitleConfig } from '../../ui-components/data-viz-ui/charts/common/common-chart-types';
import {
  useChartTitleForOverTimeChart,
  useMetricDisplayValues,
  useMetricQueries,
} from '../common/hooks';
import {
  DataOrError,
  ExternalChartComponentProps,
  MetricQueryConfig,
} from '../common/types';
import { getOvertimeBenchmarkTimeSelection } from '../common/utils';
import {
  OverTimeSegmentedVizConfig,
  OverTimeSegmentedVizData,
  OverTimeSegmentedVizDataResponseForMetric,
  OverTimeSegmentedVizDataResponseForMetricForDate,
  OverTimeSegmentedVizDataResponseForMetricForDateWithoutBenchmark,
  OverTimeSegmentedVizDataResponseForMetricWithoutBenchmark,
} from './types';

type ChartTitleOverTimeProps = {
  config: OverTimeSegmentedVizConfig;
  data: OverTimeSegmentedVizDataResponseForMetric | null;
  externalProps: ExternalChartComponentProps;
};

const useChartTitleOverTime = (props: ChartTitleOverTimeProps): TitleConfig => {
  const { config, data, externalProps } = props;
  const { query } = config;
  const metrics = [query.metric];
  const filters = [query.filters];
  return useChartTitleForOverTimeChart({
    externalProps: props.externalProps,
    // TODO: Add runtime validation for this, and also
    // improve the types so this doesn't need to be done
    metrics: metrics as MetricIdTypeArrays,
    filters: filters.join(','),
    timeSelection: config.timeSelection,
    metricsSql: data ? [data.metricSql] : [],
    segmentations: [],
    titleFromConfig: config.title,
  });
};

// null will be provided for benchmark input
// having to do this coz of rule of hooks
type QueryConfig = Pick<
  OverTimeSegmentedVizConfig,
  'query' | 'timeSelection' | 'segmentBy'
>;

function useOverTimeSegmentedQueryConfigToDataResponse(
  config: null,
  externalProps: ExternalChartComponentProps
): DataOrError<null>;
function useOverTimeSegmentedQueryConfigToDataResponse(
  config: QueryConfig,
  externalProps: ExternalChartComponentProps
): DataOrError<OverTimeSegmentedVizDataResponseForMetricWithoutBenchmark>;
function useOverTimeSegmentedQueryConfigToDataResponse(
  config: QueryConfig | null,
  externalProps: ExternalChartComponentProps
): DataOrError<OverTimeSegmentedVizDataResponseForMetricWithoutBenchmark | null>;
function useOverTimeSegmentedQueryConfigToDataResponse(
  config: QueryConfig | null,
  externalProps: ExternalChartComponentProps
): DataOrError<OverTimeSegmentedVizDataResponseForMetricWithoutBenchmark | null> {
  const { timeSliderState, segmentationState, filtersState, locale } =
    externalProps;
  let mainQuery: MetricQueryConfig | null = null;
  if (config) {
    const { query, timeSelection, segmentBy } = config;
    mainQuery = {
      metrics: [query.metric] as MetricIdTypeArrays,
      timeSelection: timeSelection,
      filters: query.filters,
      segmentations: segmentBy ? [segmentBy] : undefined,
    };
  }

  const dataFieldValueFormatter = useDataFieldValueFormatter();
  // TODO: This might work a bit weirdly with
  // time slider state
  // I think ideally useMetricQueries should not take timeSliderState
  // as input, but rather the generate query should be based on the
  // time slider input

  const { queryResponses } = useMetricQueries({
    queries: mainQuery === null ? [] : [mainQuery],
    timeSliderState,
    segmentationState,
    filtersState,
  });
  if (!config) {
    // for benchmark input, config will be null but we still need to call the hook
    return {
      data: null,
      error: null,
    };
  }
  const { segmentBy } = config;
  const mainMetricResult = queryResponses[0]?.data?.[0];
  const mainSegments = mainMetricResult?.segments ?? [];
  const data: OverTimeSegmentedVizDataResponseForMetricForDateWithoutBenchmark[] =
    mainSegments.map((segment) => {
      const dateLabel = toTimeSegmentLabel(
        segment.timeSegment,
        locale.selected
      );
      const groupSegmentsWithValues = segment.groupSegments.filter(
        (groupSegment) => groupSegment.data !== null && groupSegment.data > 0
      );
      const datapoints = groupSegmentsWithValues.map((groupSegment) => {
        // note: I think groupSegment[0] works under the assumption that
        // we segment by only one field
        const mainGroupSegment = groupSegment.groupSegment[0];
        const datapoint = {
          value: (groupSegment.data ?? 0) as number,
        };
        const segmentVal = extractSegmentValue(
          mainGroupSegment.value,
          segmentBy
        ) as string | null;
        return {
          segmentVal: segmentVal,
          datapoint,
        };
      });
      return {
        isSegmented: true,
        dateLabel,
        segmentBy,
        datapoints,
      };
    });
  const response = {
    metric: config.query.metric,
    metricSql: mainMetricResult?.meta.metricSql ?? '',
    segmentBy: segmentBy,
    data,
  };
  return {
    data: response,
    error: null,
  };
}

const combineMainDataWithBenchmark = (
  // This assumes that the order of mainRes and benchmarkRes arrays
  // is the same, i.e. benchmarkData[i] will correspond to mainData[i]
  mainData: OverTimeSegmentedVizDataResponseForMetricForDateWithoutBenchmark[],
  benchmarkData: OverTimeSegmentedVizDataResponseForMetricForDateWithoutBenchmark[]
): OverTimeSegmentedVizDataResponseForMetricForDate<true>[] => {
  const data: OverTimeSegmentedVizDataResponseForMetricForDate<true>[] =
    mainData.map((d, i) => {
      return {
        dateLabel: d.dateLabel,
        datapoints: d.datapoints.map((d) => {
          const benchmarkDataPoint = benchmarkData[i].datapoints.find(
            (benchD) => benchD.segmentVal === d.segmentVal
          );
          return {
            segmentVal: d.segmentVal,
            datapoint: {
              hasBenchmark: true,
              value: d.datapoint.value,
              benchmarkData: {
                value: benchmarkDataPoint?.datapoint.value ?? 0,
                benchmarkDateLabel: benchmarkData[i].dateLabel,
              },
            },
          };
        }),
      };
    });
  return data;
};

const useOverTimeSegmentedConfigToDataResponse = (
  config: OverTimeSegmentedVizConfig,
  externalProps: ExternalChartComponentProps
): DataOrError<OverTimeSegmentedVizDataResponseForMetric> => {
  const { query, segmentBy, benchmarkConfig, timeSelection } = config;
  const mainQueryConfig: QueryConfig = {
    query,
    timeSelection,
    segmentBy,
  };
  let benchmarkQueryConfig: QueryConfig | null = null;
  if (benchmarkConfig) {
    const benchmarkTimeSelection = getOvertimeBenchmarkTimeSelection(
      timeSelection,
      benchmarkConfig.benchmarkPeriod
    );
    benchmarkQueryConfig = {
      query,
      timeSelection: benchmarkTimeSelection,
      segmentBy,
    };
  }
  const mainData = useOverTimeSegmentedQueryConfigToDataResponse(
    mainQueryConfig,
    externalProps
  );
  const benchmarkData = useOverTimeSegmentedQueryConfigToDataResponse(
    benchmarkQueryConfig,
    externalProps
  );
  const error = mainData.error || benchmarkData.error;
  if (error) {
    return {
      data: null,
      error,
    };
  }
  if (benchmarkData.data === null) {
    const dataWithHasBenchmarkInfo: OverTimeSegmentedVizDataResponseForMetric<false> =
      produce(mainData.data, (draft) => {
        draft.data = draft.data.map((d) => {
          return {
            ...d,
            datapoints: d.datapoints.map((d) => {
              return {
                ...d,
                datapoint: {
                  value: d.datapoint.value,
                  hasBenchmark: false as const,
                },
              };
            }),
          };
        });
      }) as OverTimeSegmentedVizDataResponseForMetric<false>;
    return {
      data: dataWithHasBenchmarkInfo,
      error: null,
    };
  } else {
    const combinedData = combineMainDataWithBenchmark(
      mainData.data.data,
      benchmarkData.data.data
    );
    const combinedResponse = {
      metric: mainData.data.metric,
      metricSql: `Main - ${mainData.data.metricSql}, Benchmark ${benchmarkData.data.metricSql}`,
      segmentBy: mainData.data.segmentBy,
      data: combinedData,
    };
    return {
      data: combinedResponse,
      error: null,
    };
  }
};

export const useOverTimeSegmentedConfigToData = (
  config: OverTimeSegmentedVizConfig,
  externalProps: ExternalChartComponentProps
): DataOrError<OverTimeSegmentedVizData> => {
  const { data, error } = useOverTimeSegmentedConfigToDataResponse(
    config,
    externalProps
  );
  const metricDetailsMap = useMetricDetailsMapContext();
  const metricDisplayValues = useMetricDisplayValues([
    config.query.metric.value,
  ] as unknown as MetricId[]);
  const titleConfig = useChartTitleOverTime({
    config,
    data,
    externalProps,
  });
  if (error) {
    return { data: null, error };
  }
  return {
    data: {
      dataResponse: data,
      titleConfig,
      metricDisplayValues,
      metricDetailsMap,
      chartColors: config.chartColors,
      showTotal: config.showTotal,
    },
    error: null,
  };
};
